<script setup lang="ts">
import { computed } from "vue";
import TextField from "@/design/primitives/TextField.vue";
import { instantOf, wallClock } from "@/shared/format";
import type { WidgetProps } from "./contract";

/**
 * Date, datetime and time — one widget, three input types.
 *
 * A `date` is kept as the plain "YYYY-MM-DD" string it arrived as. Turning it
 * into a Date and back shifts the day for anyone west of UTC, which is the bug
 * that makes a birthday land on the wrong date for half the users.
 *
 * A `datetime` is an instant, so it round-trips through ISO — mas o mostrador
 * dessa volta é o do WORKSPACE e nunca o do browser. Lido na hora de quem tem a
 * aba aberta, "9h" escrito num browser em UTC ficava gravado como 09:00Z e o
 * calendário, que desenha no fuso do tenant, mostrava 6h (28/08). O fuso é um
 * facto do negócio: ver `shared/format/wall-clock.ts`.
 *
 * A `time` is wall clock and carries no zone at all.
 *
 * `config.future` põe um `min` no controle: o calendário do browser desenha o
 * passado apagado, que é onde o erro se evita melhor — antes de existir. A
 * recusa de verdade continua no validador (o `min` nativo não cobre quem cola
 * um valor, e o mostrador congelado no mount envelhece enquanto a pessoa
 * escreve).
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const inputType = computed(() => {
	if (props.field.type === "time") return "time";
	if (props.field.type === "datetime") return "datetime-local";
	return "date";
});

const text = computed(() => {
	const value = props.modelValue;
	if (value === null || value === undefined || value === "") return "";
	// `datetime-local` wants "YYYY-MM-DDTHH:MM": no zone marker, workspace's clock.
	if (props.field.type === "datetime") return wallClock(String(value));
	// date and time pass through untouched.
	return String(value).slice(0, props.field.type === "time" ? 5 : 10);
});

/**
 * O chão do controle, no mostrador do workspace. Calculado uma vez: um `min`
 * que avança sozinho invalidaria, a meio da escrita, o valor que a pessoa
 * acabou de escolher.
 */
const floor = computed(() => {
	if (props.field.config.future !== true) return undefined;
	if (props.field.type === "datetime") return wallClock(new Date());
	if (props.field.type === "date") return wallClock(new Date()).slice(0, 10);
	return undefined;
});

function change(raw: string): void {
	if (raw === "") {
		emit("update:modelValue", null);
		return;
	}
	if (props.field.type === "datetime") {
		emit("update:modelValue", instantOf(raw));
		return;
	}
	emit("update:modelValue", raw);
}
</script>

<template>
	<TextField
		:id="controlId"
		:model-value="text"
		:type="inputType"
		:disabled="disabled"
		:invalid="Boolean(error)"
		:described-by="describedBy"
		:min="floor"
		@update:model-value="change($event)"
		@blur="emit('blur')"
	/>
</template>
